
import React, { useEffect, useRef, useState } from 'react';
import OrientationContext, { OrientationPermissionStatus, OrientationValue } from './OrientationContext';

type Props = {
	children?: React.ReactNode;
};


function norm180(deg: number) {
	// maps to (-180, 180]
	let x = ((deg + 180) % 360 + 360) % 360 - 180;
	// treat -180 as +180 (same physical orientation)
	if (x === -180) x = 180;
	return x;
}

// right landscape example:  {alpha: 345.98727188411334, beta: 1.8673105034092454, gamma: 66.51885235120007}
// left landscape example:  {alpha: 174.97278504013545, beta: 1.2503306343168368, gamma: -63.77047306359195}

function getOrientationFromAlphaBetaGamma(
	{ alpha, beta, gamma }: { alpha: number; beta: number; gamma: number }
): OrientationValue {
	const b = norm180(beta);
	const g = norm180(gamma);

	if (Math.abs(b) < 15 && Math.abs(g) < 15) return "unknown";

	// LANDSCAPE
	if (Math.abs(g) > 45) {
		// base side from gamma sign
		let side: OrientationValue = g > 0 ? "right" : "left";

		// if beta is near ±180, flip the side
		if (Math.abs(b) > 90) {
			side = side === "left" ? "right" : "left";
		}

		return side;
	}

	// PORTRAIT
	return b >= 0 ? "bottom" : "top";
}

function getWindowOrientation(): OrientationValue {
    const angle = window.screen.orientation?.angle ?? window.orientation ?? 0;
    if (angle === 0) return 'bottom';
    if (angle === 180) return 'top';
    if (angle === 90) return 'left';
    if (angle === -90 || angle === 270) return 'right';
    return 'unknown';
}

export default function OrientationProvider({ children }: Props) {
	const [orientation, setOrientation] = useState<OrientationValue>('unknown');
	const [sensorOrientation, setSensorOrientation] = useState<OrientationValue>('unknown');
	const [windowOrientation, setWindowOrientation] = useState<OrientationValue>(getWindowOrientation());
	const [enabled, setEnabled] = useState(false);
	const [orientationPermissionStatus, setOrientationPermissionStatus] = useState<OrientationPermissionStatus>('not_requested');
	const listenerRef = useRef<(e: DeviceOrientationEvent) => void | null>(null);
	// Ref guard prevents a second call while the iOS permission dialog is open (state hasn't updated yet)
	const requestingRef = useRef(false);


	const enableOrientation = () => {
		if (enabled || requestingRef.current) return;
		// iOS 13+ requires an explicit permission request AND it must be called
		// from a user-gesture handler (click/touch). Calling from a useEffect is
		// unreliable on iOS — prefer calling this from a button onClick where possible.
		if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
			requestingRef.current = true;
			(DeviceOrientationEvent as any).requestPermission()
				.then((result: string) => {
					if (result === 'granted') {
						setOrientationPermissionStatus('granted');
						setEnabled(true);
					} else {
						setOrientationPermissionStatus('denied');
						console.error('orientation permission denied');
					}
				})
				.catch(() => {
					// iOS throws here if called outside a user gesture
					setOrientationPermissionStatus('denied');
					console.error('orientation permission request failed — was this called outside a user gesture?');
				})
				.finally(() => {
					requestingRef.current = false;
				});
		} else {
			// Android / desktop — no permission gate, events fire freely
			setOrientationPermissionStatus('not_required');
			setEnabled(true);
		}
	};


    function adjust_orientation(orientation_from_window: OrientationValue, orientation_from_sensor: OrientationValue): OrientationValue {
        // orientation from gravity assumes the phone is in portrait mode,  it needs adjusting for landscape mode
        if (orientation_from_window === 'unknown') return orientation_from_sensor;
        if (orientation_from_window === 'left') {
            if (orientation_from_sensor === 'top') return 'right';
            if (orientation_from_sensor === 'bottom') return 'left';
            if (orientation_from_sensor === 'left') return 'bottom';
            if (orientation_from_sensor === 'right') return 'top';
        } else if (orientation_from_window === 'right') {
            if (orientation_from_sensor === 'top') return 'left';
            if (orientation_from_sensor === 'bottom') return 'right';
            if (orientation_from_sensor === 'left') return 'bottom';
            if (orientation_from_sensor === 'right') return 'bottom';
        } else if (orientation_from_window === 'bottom') {
			if (orientation_from_sensor === 'left') return 'right';
			if (orientation_from_sensor === 'right') return 'left';

		}


        return orientation_from_sensor;
    }

	useEffect(() => {
		if (!enabled) return;
		const handleOrientation = (event: DeviceOrientationEvent) => {
			// event.gamma: left/right, event.beta: front/back
			// We'll use these to estimate gravity direction
			// gamma: -90 (left) to 90 (right)
			// beta: -180 (top) to 180 (bottom)
			const latest_orientation = {
				alpha: event.alpha ?? 0,
				beta: event.beta ?? 0,
				gamma: event.gamma ?? 0,
			};
            const orientation_from_window = getWindowOrientation();
            const orientation_from_sensor = getOrientationFromAlphaBetaGamma(latest_orientation);
            const final_orientation = adjust_orientation(orientation_from_window, orientation_from_sensor);
			setOrientation((prev)=> {
				if (prev !== final_orientation) {
					console.log(`Orientation changed to: ${final_orientation} - sensor: ${orientation_from_sensor}, window: ${orientation_from_window}`);
					setSensorOrientation(orientation_from_sensor);
					setWindowOrientation(orientation_from_window);
					return final_orientation;
				}
				return prev;
			});

		};
    //@ts-ignore
		listenerRef.current = handleOrientation;
		window.addEventListener('deviceorientation', handleOrientation, true);
		return () => {
			if (listenerRef.current) {
				window.removeEventListener('deviceorientation', listenerRef.current, true);
			}
		};
	}, [enabled]);

	const value = {
		orientation,
		sensorOrientation,
		windowOrientation,
		orientationPermissionStatus,
		enableOrientation
	};

	return (
		<OrientationContext.Provider value={value}>
			{children}
		</OrientationContext.Provider>
	);
}
